import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import { faStar, faUsers, faVideo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { pluralize } from '@/utils/helpers'

import type { ProgressionLevel } from '@/queries/constants'
import { useUser } from '@/client/context/user'
import { getUserUrl } from '@/client/services/url'

import ShwLink from '@/components/ShwLink'
import { TUTOR_BIO_DEFAULT } from '@/components/Tutor/common'
import Tippy from '@/components/UI/Tippy'
import UserAvatar from '@/components/UserAvatar'

function RowCountContainer({
  text,
  RowIcon,
}: {
  text: string
  RowIcon: JSX.Element
}): React.ReactElement {
  return (
    <Stack direction="row" align="center" spacing="8px" px="8px">
      <Box>{RowIcon}</Box>
      <Text>{text}</Text>
    </Stack>
  )
}

export default function TutorCard({
  tutorId,
  username,
  displayName,
  superHelpfulCount,
  pastSessionCount,
  availableSessions,
  followCount,
  bio,
  progressionLevel,
  avatarUrl,
  noOfBioLines = 5,
  openExternally = false,
  hideFooter = false,
}: {
  tutorId: number
  username: string | null
  displayName: string | null
  superHelpfulCount: number
  pastSessionCount: number
  availableSessions: number
  followCount: number
  bio: string
  progressionLevel: ProgressionLevel
  avatarUrl: string
  noOfBioLines?: number
  openExternally?: boolean
  hideFooter?: boolean
}): JSX.Element {
  const currentUser = useUser()
  const isCurrentUser = tutorId === currentUser.id
  const maxBioLength = 600

  const trimmedBio = bio.length > maxBioLength ? `${bio.slice(0, maxBioLength).trim()}...` : bio

  return (
    <ShwLink href={getUserUrl({ userId: tutorId ?? 0, username })} isExternal={openExternally}>
      <Box
        color="inherit"
        cursor="pointer"
        textDecoration="none"
        backgroundColor="bg"
        width="100%"
        _hover={{
          filter: 'brightness(95%)',
          border: `2px solid ${chakraTokenToVar('border.emphasized')}`,
        }}
        borderRadius="8px"
        border={`2px solid ${chakraTokenToVar('border')}`}
      >
        <Flex backgroundColor="bg.muted" justifyContent="center" borderTopRadius="8px">
          <UserAvatar
            src={avatarUrl}
            name={`${displayName}`}
            border={`5px solid ${chakraTokenToVar('bg')}`}
            size="xl"
            position="relative"
            top="16px"
            progressionLevel={progressionLevel}
          />
        </Flex>
        <Box mt="32px" px="16px" mb="24px">
          <Box mb="8px" display="flex" alignItems="center" justifyContent="left">
            <Text fontWeight="bold" fontSize="large" mr="8px">
              {`${displayName}${isCurrentUser ? ' (you)' : ''}`}
            </Text>
            {username != null ? (
              <Text color="fg.muted" fontSize="large" wordBreak="break-word">
                {`@${username}`}
              </Text>
            ) : null}
          </Box>
          <Text mb="16px" color="fg.muted" fontSize="small">
            {pluralize(availableSessions, 'upcoming session')}
          </Text>
          <Text
            as="span"
            mb="8px"
            noOfLines={noOfBioLines}
            color={trimmedBio.length === 0 ? 'fg.subtle' : 'fg'}
            minHeight={`${noOfBioLines * 24}px`}
          >
            {trimmedBio.length === 0 ? TUTOR_BIO_DEFAULT : trimmedBio}
          </Text>
        </Box>
        {hideFooter ? null : (
          <Box display={['block', 'none', 'block']} pb="16px">
            <Flex flexDir="row" justifyContent="center" color="fg.subtle">
              {pastSessionCount > 0 ? (
                <Tippy content={pluralize(pastSessionCount, 'Past Session')}>
                  <RowCountContainer
                    text={`${pastSessionCount}`}
                    RowIcon={<FontAwesomeIcon icon={faVideo} />}
                  />
                </Tippy>
              ) : null}
              {followCount > 0 ? (
                <Tippy content={pluralize(followCount, 'Follower')}>
                  <RowCountContainer
                    text={`${followCount}`}
                    RowIcon={<FontAwesomeIcon icon={faUsers} />}
                  />
                </Tippy>
              ) : null}
              {superHelpfulCount > 0 ? (
                <Tippy content={pluralize(superHelpfulCount, '"Super Helpful" Rating')}>
                  <RowCountContainer
                    text={`${superHelpfulCount}`}
                    RowIcon={<FontAwesomeIcon icon={faStar} />}
                  />
                </Tippy>
              ) : null}
            </Flex>
          </Box>
        )}
      </Box>
    </ShwLink>
  )
}
